/**
 * EVI-1794 interim Snowplow handlers until a full snowtype regen.
 *
 * Schemas landed on Snowplow DEV via https://github.com/oscerai/snowplow/pull/224
 * and were promoted to PROD via https://github.com/oscerai/snowplow/pull/228.
 *
 * Delete this file after `npx @snowplow/snowtype generate` once tracking-plan
 * publish is confirmed and generated `*Spec` trackers replace these handlers.
 */
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import { createEventSpecification } from '@snowtype/snowplow';

const MODELS_DATA_PRODUCT_ID = '55c3ff1a-524d-4d4f-8413-267c7e163c13';

type SnowplowPayload = Record<string, unknown> & {
  context?: unknown;
  timestamp?: number;
};

type SelfDescribingContext = {
  schema: string;
  data: Record<string, unknown>;
};

function trackEvidenceEventSpec(
  schema: string,
  specId: string,
  name: string,
  payload: SnowplowPayload
): void {
  const { context, timestamp, ...data } = payload;
  const eventSpecificationContext = createEventSpecification({
    id: specId,
    version: 0,
    name,
    data_product_id: MODELS_DATA_PRODUCT_ID,
    data_product_name: 'models',
  }) as SelfDescribingContext;

  const priorContext: SelfDescribingContext[] = Array.isArray(context)
    ? (context as SelfDescribingContext[])
    : [];

  trackSelfDescribingEvent({
    event: { schema, data },
    context: [...priorContext, eventSpecificationContext],
    timestamp,
  });
}

export function trackEvidencePromptsBarShownSpec(payload: SnowplowPayload): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_prompts_bar_shown/jsonschema/1-0-0',
    '7674f9a4-1a77-4e34-9b1c-09321d46e296',
    'evidence_prompts_bar_shown',
    payload
  );
}

export function trackEvidencePromptsBarDismissedSpec(payload: SnowplowPayload): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_prompts_bar_dismissed/jsonschema/1-0-0',
    '8fd79ac8-0dfc-4822-9ea9-3c5ca21f2914',
    'evidence_prompts_bar_dismissed',
    payload
  );
}

export function trackEvidencePromptsBarExpandedSpec(payload: SnowplowPayload): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_prompts_bar_expanded/jsonschema/1-0-0',
    'dfebba8d-5a9e-42cc-9500-58f7e700a893',
    'evidence_prompts_bar_expanded',
    payload
  );
}

// Ask Heidi shortcuts + Sources picker — schemas via
// https://github.com/oscerai/snowplow/pull/259 (models tracking plan).
export function trackEvidenceShortcutsOpenedSpec(payload: SnowplowPayload): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_shortcuts_opened/jsonschema/1-0-0',
    'dd205bf2-302d-4455-ad7c-b283bf2b3440',
    'evidence_shortcuts_opened',
    payload
  );
}

export function trackEvidenceShortcutRunSpec(payload: SnowplowPayload): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_shortcut_run/jsonschema/1-0-0',
    'a33580bd-3070-4816-a4d5-fb7c74d38c90',
    'evidence_shortcut_run',
    payload
  );
}

export function trackEvidenceShortcutSaveToggledSpec(payload: SnowplowPayload): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_shortcut_save_toggled/jsonschema/1-0-0',
    'c3f50069-8138-49c6-9996-eec27d049ed0',
    'evidence_shortcut_save_toggled',
    payload
  );
}

export function trackEvidenceShortcutSearchUsedSpec(payload: SnowplowPayload): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_shortcut_search_used/jsonschema/1-0-0',
    '1ef83150-56a9-4a8b-9df0-20a4db1ea12c',
    'evidence_shortcut_search_used',
    payload
  );
}

export function trackEvidenceSourcesOpenedSpec(payload: SnowplowPayload): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_sources_opened/jsonschema/1-0-0',
    '24d60f1a-c698-4318-8b67-3aa85df34ef7',
    'evidence_sources_opened',
    payload
  );
}

export function trackEvidenceSummarySuggestionShownSpec(payload: SnowplowPayload): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_summary_suggestion_shown/jsonschema/1-0-0',
    '0a3473c6-90ca-49ec-ac12-55af627b3ab4',
    'evidence_summary_suggestion_shown',
    payload
  );
}

export function trackEvidenceSummarySuggestionDismissedSpec(
  payload: SnowplowPayload
): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_summary_suggestion_dismissed/jsonschema/1-0-0',
    'a9178077-8399-4f97-8e96-cbe44fa75026',
    'evidence_summary_suggestion_dismissed',
    payload
  );
}

export function trackEvidenceSummarySuggestionGeneratedSpec(
  payload: SnowplowPayload
): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_summary_suggestion_generated/jsonschema/1-0-0',
    '526a739e-93dd-4845-a291-c0bc3553b115',
    'evidence_summary_suggestion_generated',
    payload
  );
}

/**
 * Interim handler for `evidence_check_other_sources_clicked` until the schema
 * (oscerai/snowplow → data-structures/com.heidi/evidence_check_other_sources_clicked.yaml,
 * models tracking plan spec d81563e7-0ceb-4c10-90e5-40f505cc41e9) is promoted
 * to PROD and Snowtype is regenerated. Replace with the generated
 * `trackEvidenceCheckOtherSourcesClickedSpec` after `npx @snowplow/snowtype generate`.
 */
export function trackEvidenceCheckOtherSourcesClickedSpec(
  payload: SnowplowPayload
): void {
  trackEvidenceEventSpec(
    'iglu:com.heidi/evidence_check_other_sources_clicked/jsonschema/1-0-0',
    'd81563e7-0ceb-4c10-90e5-40f505cc41e9',
    'evidence_check_other_sources_clicked',
    payload
  );
}
